export const EDIT_TRANSACTION_RULE_ACTION = "EDIT_TRANSACTION";

export type TransactionEditRulePayload = {
  transaction_date?: string;
  name?: string;
  extra_info?: string | null;
  amount?: number;
  currency?: string;
  subcategory_code?: string;
  budget_category_id?: string | null;
};

type EditableTransactionShape = {
  transactionEditDefaults?: TransactionEditRulePayload;
  amount?: {
    originalAmount?: {
      amount?: string | number;
      currency?: string;
    };
    chargedAmount?: {
      amount?: string | number;
      currency?: string;
    };
  };
  description?: {
    additionalInfo?: unknown;
    description?: string;
    fixedText?: string;
  };
  category?: {
    main?: string;
    sub?: string;
  };
  changedCategory?: {
    main?: string;
    sub?: string;
  };
  date?: {
    valueDate?: string;
    bookingDate?: string;
    transactionDate?: string;
  };
  calculatedCategory?: string;
  details?: string;
  transactionDescription?: string;
  merchantName?: string;
  budgetSubCategoryName?: string;
  budgetCategoryId?: string | null;
};

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function optionalNullableString(value: unknown): string | null | undefined {
  if (value === null) return null;
  return optionalString(value);
}

export function parseTransactionEditRulePayload(
  value: string | null | undefined
): TransactionEditRulePayload | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (!isObject(parsed)) return null;
    const amount =
      parsed.amount === null || parsed.amount === undefined
        ? undefined
        : Number(parsed.amount);
    return {
      transaction_date: optionalString(parsed.transaction_date),
      name: optionalString(parsed.name),
      extra_info: optionalNullableString(parsed.extra_info),
      amount: Number.isFinite(amount) ? amount : undefined,
      currency: optionalString(parsed.currency)?.toUpperCase(),
      subcategory_code: optionalString(parsed.subcategory_code),
      budget_category_id: optionalNullableString(parsed.budget_category_id),
    };
  } catch {
    return null;
  }
}

export function serializeTransactionEditRulePayload(
  payload: TransactionEditRulePayload
): string {
  return JSON.stringify({
    transaction_date: payload.transaction_date,
    name: payload.name,
    extra_info: payload.extra_info ?? null,
    amount: payload.amount,
    currency: payload.currency,
    subcategory_code: payload.subcategory_code,
    budget_category_id: payload.budget_category_id ?? null,
  });
}

export function applyTransactionEditPayload<T extends EditableTransactionShape>(
  transaction: T,
  payload: TransactionEditRulePayload | null
): T {
  if (!payload) return transaction;

  if (!transaction.transactionEditDefaults) {
    const amount = Number(transaction.amount?.chargedAmount?.amount);
    transaction.transactionEditDefaults = {
      transaction_date:
        transaction.date?.transactionDate || transaction.date?.valueDate,
      name: transaction.description?.description || transaction.merchantName,
      extra_info:
        transaction.transactionDescription ||
        transaction.details ||
        (typeof transaction.description?.additionalInfo === "string"
          ? transaction.description.additionalInfo
          : null),
      amount: Number.isFinite(amount) ? amount : undefined,
      currency: transaction.amount?.chargedAmount?.currency,
      subcategory_code:
        transaction.changedCategory?.sub ||
        transaction.calculatedCategory ||
        transaction.category?.sub,
      budget_category_id: transaction.budgetCategoryId ?? null,
    };
  }

  if (payload.transaction_date && transaction.date) {
    transaction.date.valueDate = payload.transaction_date;
    transaction.date.bookingDate = payload.transaction_date;
    transaction.date.transactionDate = payload.transaction_date;
  }

  if (payload.name && transaction.description) {
    transaction.description.description = payload.name;
    transaction.description.fixedText = payload.name;
    transaction.merchantName = payload.name;
  }

  if (payload.extra_info !== undefined) {
    const extraInfo = payload.extra_info || "";
    if (transaction.description) {
      transaction.description.additionalInfo = extraInfo;
    }
    transaction.details = extraInfo;
    transaction.transactionDescription = extraInfo;
  }

  if (payload.amount !== undefined && transaction.amount) {
    const amount = String(payload.amount);
    if (transaction.amount.originalAmount) {
      transaction.amount.originalAmount.amount = amount;
    }
    if (transaction.amount.chargedAmount) {
      transaction.amount.chargedAmount.amount = amount;
    }
  }

  if (payload.currency && transaction.amount) {
    if (transaction.amount.originalAmount) {
      transaction.amount.originalAmount.currency = payload.currency;
    }
    if (transaction.amount.chargedAmount) {
      transaction.amount.chargedAmount.currency = payload.currency;
    }
  }

  if (payload.subcategory_code) {
    if (transaction.category) {
      transaction.category.main = payload.subcategory_code;
      transaction.category.sub = payload.subcategory_code;
    }
    transaction.changedCategory = {
      main: payload.subcategory_code,
      sub: payload.subcategory_code,
    };
    transaction.calculatedCategory = payload.subcategory_code;
    transaction.budgetSubCategoryName = payload.subcategory_code;
  }

  if (payload.budget_category_id !== undefined) {
    transaction.budgetCategoryId = payload.budget_category_id;
  }

  return transaction;
}
